/**
 * system-admin layout — route group wrapping all /system-admin/*
 * pages with the SystemAdminGuard + a minimal nav shell.
 *
 * autoCodeSplitting: true in vite.config means TanStack Router emits this
 * group as a separate JS chunk, reducing the main-user bundle.
 *
 * Nav items are placeholder stubs; the full sidebar nav can extend this shell.
 */

import { createFileRoute, Outlet, Link, useLocation } from "@tanstack/react-router";
import { requireAuth } from "@/lib/auth/require-auth";
import { SystemAdminGuard } from "@/features/system-admin/components/system-admin-guard";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";
import {
  ArrowLeft,
  LayoutDashboard,
  Users,
  Building2,
  ScrollText,
  ShieldCheck,
  Settings,
} from "lucide-react";
import { buttonVariants } from "@/components/ui/button";

export const Route = createFileRoute("/system-admin")({
  beforeLoad: async ({ location }) => {
    // Auth is required; org is NOT required for system-admin access.
    await requireAuth(location.href);
  },
  component: SystemAdminLayout,
});

// Nav entries for the system-admin sidebar shell.
// Icons: Lucide only — zero emoji (project rule).
// `to` values match TanStack Router registered route paths (FileRoutesByTo).
const NAV_ITEMS = [
  {
    to: "/system-admin" as const,
    label: "nav.dashboard",
    icon: LayoutDashboard,
    activePath: "/system-admin",
    exact: true,
  },
  {
    to: "/system-admin/users" as const,
    label: "nav.users",
    icon: Users,
    activePath: "/system-admin/users",
    exact: false,
  },
  {
    to: "/system-admin/orgs" as const,
    label: "nav.orgs",
    icon: Building2,
    activePath: "/system-admin/orgs",
    exact: false,
  },
  {
    to: "/system-admin/audit" as const,
    label: "nav.audit",
    icon: ScrollText,
    activePath: "/system-admin/audit",
    exact: false,
  },
  {
    to: "/system-admin/admins" as const,
    label: "nav.admins",
    icon: ShieldCheck,
    activePath: "/system-admin/admins",
    exact: false,
  },
  {
    to: "/system-admin/settings" as const,
    label: "nav.settings",
    icon: Settings,
    activePath: "/system-admin/settings",
    exact: false,
  },
];

function SystemAdminLayout() {
  const t = useTranslations("system-admin.common");
  const location = useLocation();

  return (
    <SystemAdminGuard>
      <div className="flex min-h-screen bg-[var(--background)]">
        {/* Sidebar nav shell — A5 replaces with full AppSidebar variant */}
        <aside className="hidden xl:flex w-56 shrink-0 flex-col border-r border-[var(--border)] bg-[var(--card)] py-4">
          <div className="px-4 pb-4 border-b border-[var(--border)]">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              {t("sidebar_label")}
            </p>
          </div>
          <nav className="flex-1 overflow-y-auto py-2 px-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.exact
                ? location.pathname === item.activePath
                : location.pathname.startsWith(item.activePath);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    "hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]",
                    isActive
                      ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
                      : "text-[var(--muted-foreground)]",
                  )}
                >
                  <Icon className="size-4 shrink-0" aria-hidden />
                  {t(item.label)}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main id="system-admin-content" className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-7xl p-4 md:p-6">
            <div className="mb-4 flex justify-end">
              <Link to="/" className={cn(buttonVariants({ variant: "outline", size: "sm" }))}>
                <ArrowLeft className="size-3.5" aria-hidden />
                {t("actions.back_to_app")}
              </Link>
            </div>
            <Outlet />
          </div>
        </main>
      </div>
    </SystemAdminGuard>
  );
}
